<template>
  <div
    :class="[
      baseContainerClass,
      sizeClass,
      variantClass
    ]"
  >
    <!-- Icon -->
    <div :class="iconContainerClass">
      <slot name="icon">
        <!-- Default document icon -->
        <component
          :is="defaultIcon"
          :class="iconClass"
          v-bind="iconProps"
        />
      </slot>
    </div>
    
    <!-- Title -->
    <h3
      v-if="title"
      :class="titleClass"
    >
      {{ title }}
    </h3>
    
    <!-- Description -->
    <p
      :class="[
        descriptionClass,
        customDescriptionClass
      ]"
    >
      <slot>{{ description }}</slot>
    </p>
    
    <!-- Actions -->
    <div
      v-if="$slots.actions"
      :class="actionsClass"
    >
      <slot name="actions" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  DocumentTextIcon,
  FolderOpenIcon,
  UserGroupIcon,
  ShoppingBagIcon,
  TableCellsIcon,
  InboxIcon,
  MagnifyingGlassIcon
} from '@heroicons/vue/24/outline';

type IconType = 'document' | 'folder' | 'users' | 'shopping' | 'table' | 'inbox' | 'search';
type SizeType = 'sm' | 'md' | 'lg' | 'xl';
type VariantType = 'default' | 'minimal' | 'card' | 'centered';

interface Props {
  title?: string;
  description?: string;
  icon?: IconType;
  size?: SizeType;
  variant?: VariantType;
  iconClass?: string;
  iconProps?: Record<string, any>;
  descriptionClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  description: '',
  icon: 'document',
  size: 'md',
  variant: 'default',
  iconClass: '',
  iconProps: () => ({}),
  descriptionClass: ''
});

// Icon mapping
const iconMap = {
  document: DocumentTextIcon,
  folder: FolderOpenIcon,
  users: UserGroupIcon,
  shopping: ShoppingBagIcon,
  table: TableCellsIcon,
  inbox: InboxIcon,
  search: MagnifyingGlassIcon
};

const defaultIcon = computed(() => iconMap[props.icon]);

// Size classes
const sizeClass = computed(() => {
  const sizes = {
    sm: 'p-6',
    md: 'p-8',
    lg: 'p-10',
    xl: 'p-12'
  };
  return sizes[props.size];
});

// Variant classes
const variantClass = computed(() => {
  const variants = {
    default: 'rounded-2xl border border-slate-200 bg-white text-center shadow-sm dark:border-slate-700 dark:bg-slate-900/70',
    minimal: 'text-center',
    card: 'bg-white dark:bg-gray-800 rounded-lg shadow text-center',
    centered: 'text-center'
  };
  return variants[props.variant];
});

// Base container class
const baseContainerClass = computed(() => {
  if (props.variant === 'minimal') return '';
  return 'text-center';
});

// Icon container classes
const iconContainerClass = computed(() => {
  const sizes = {
    sm: 'mb-3',
    md: 'mb-4',
    lg: 'mb-5',
    xl: 'mb-6'
  };
  return `flex justify-center ${sizes[props.size]}`;
});

// Icon classes
const iconClass = computed(() => {
  const sizes = {
    sm: 'h-10 w-10',
    md: 'h-12 w-12',
    lg: 'h-16 w-16',
    xl: 'h-20 w-20'
  };
  const colors = {
    default: 'text-slate-400',
    minimal: 'text-slate-400',
    card: 'text-gray-400',
    centered: 'text-slate-400'
  };
  return `${sizes[props.size]} ${colors[props.variant]} ${props.iconClass}`;
});

// Title classes
const titleClass = computed(() => {
  const sizes = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl'
  };
  return `mb-2 font-semibold text-slate-900 dark:text-slate-100 ${sizes[props.size]}`;
});

// Description classes
const descriptionClass = computed(() => {
  const sizes = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
    xl: 'text-lg'
  };
  return `text-slate-600 dark:text-slate-300 ${sizes[props.size]}`;
});

// Custom description class
const customDescriptionClass = computed(() => props.descriptionClass);

// Actions classes
const actionsClass = computed(() => {
  const sizes = {
    sm: 'mt-4',
    md: 'mt-6',
    lg: 'mt-8',
    xl: 'mt-10'
  };
  return sizes[props.size];
});
</script>
